import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';

import { useIntl } from '../../../../util/reactIntl';
import {
  fetchAvailableFavoritesCount,
  selectFavorites,
  selectFavoritesBadgeCount,
} from '../../../../ducks/favorites.duck';

import { IconClose, LinkedLogo, NamedLink } from '../../../../components';
import CategoryMenu from '../../../../components/CategoryMenu/CategoryMenu';

import IconHeart from '../icons/IconHeart';
import IconMessages from '../icons/IconMessages';
import IconSearch from '../icons/IconSearch';
import IconUser from '../icons/IconUser';

import UserMenu from '../UserMenu/UserMenu';
import TopbarSearchForm from './TopbarSearchForm/TopbarSearchForm';

import css from './TopbarDesktop.module.css';

/**
 * Desktop topbar. Logo on the left, right-side actions on the right:
 * search / heart / bag placeholders followed by either a "user" login
 * icon (unauth) or the UserMenu avatar dropdown (auth). The category
 * navigation lives in a separate `<CategoryMenu />` row beneath the
 * topbar so this chrome stays light.
 *
 * @param {Object} props
 * @param {boolean} props.isAuthenticated
 * @param {Object} [props.currentUser]
 * @param {Function} [props.onLogout]
 * @returns {JSX.Element}
 */
const TopbarDesktop = props => {
  const { isAuthenticated, currentUser, onLogout, compact = false } = props;
  const intl = useIntl();

  const dispatch = useDispatch();
  // The badge counts only favorites that are still buyable, matching what
  // the favorites page lists (sold items are filtered out there). Re-checked
  // whenever the saved list changes.
  const favoritesKey = useSelector(state => selectFavorites(state).join(','));
  const favoritesCount = useSelector(selectFavoritesBadgeCount);
  useEffect(() => {
    dispatch(fetchAvailableFavoritesCount());
  }, [dispatch, favoritesKey]);
  // Combined sale + order notification count from user.duck — same
  // source the inbox red-dot already reads from. Drives a small dot on
  // the messages icon so users know they have something new without
  // having to click in. Qualitative signal only, not a count number —
  // breakdown lives in the inbox.
  const messagesNotificationCount = useSelector(
    state =>
      (state.user?.currentUserSaleNotificationCount || 0) +
      (state.user?.currentUserOrderNotificationCount || 0)
  );

  // Inline search bar state — toggled by the search icon. The
  // CategoryMenu (left slot) unmounts while open so the search form
  // can take that wider slot; the logo stays anchored in the centre
  // and the action icons stay anchored on the right. Compact mode
  // (checkout / signup chrome) skips the toggle entirely and keeps
  // the original NamedLink behaviour so we don't encourage task
  // abandonment mid-checkout.
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const searchToggleRef = useRef(null);
  const headerRef = useRef(null);

  const closeSearch = useCallback(() => {
    setIsSearchOpen(false);
    // Return focus to the toggle so screen-reader and keyboard users
    // don't lose their place when the inline form collapses.
    requestAnimationFrame(() => {
      if (searchToggleRef.current) {
        searchToggleRef.current.focus();
      }
    });
  }, []);

  // Esc closes; mousedown outside the header closes (clicking the
  // toggle itself is inside the header so it cleanly toggles instead).
  useEffect(() => {
    if (!isSearchOpen) {
      return undefined;
    }
    const onKeyDown = e => {
      if (e.key === 'Escape') {
        closeSearch();
      }
    };
    const onMouseDown = e => {
      if (headerRef.current && !headerRef.current.contains(e.target)) {
        closeSearch();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onMouseDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onMouseDown);
    };
  }, [isSearchOpen, closeSearch]);

  const showInlineSearch = isSearchOpen && !compact;

  return (
    <header className={css.root} ref={headerRef}>
      <div className={classNames(css.inner, { [css.innerCompact]: compact })}>
        {compact ? null : showInlineSearch ? (
          <TopbarSearchForm onClose={closeSearch} className={css.searchFormSlot} />
        ) : (
          <CategoryMenu className={css.categoryMenu} />
        )}

        <LinkedLogo
          className={classNames(css.logoLink, { [css.logoLinkCompact]: compact })}
          layout="desktop"
          logoImageClassName={css.logoImage}
          alt={intl.formatMessage({ id: 'Topbar.logoAlt' })}
        />

        <div className={css.actions}>
          {compact ? (
            <NamedLink
              className={css.iconButton}
              name="SearchPage"
              ariaLabel={intl.formatMessage({ id: 'Topbar.actionSearch' })}
            >
              <IconSearch className={css.icon} />
            </NamedLink>
          ) : (
            <button
              ref={searchToggleRef}
              type="button"
              className={css.iconButton}
              aria-label={intl.formatMessage({
                id: isSearchOpen ? 'Topbar.closeSearch' : 'Topbar.actionSearch',
              })}
              aria-expanded={isSearchOpen}
              onClick={() => (isSearchOpen ? closeSearch() : setIsSearchOpen(true))}
            >
              {isSearchOpen ? (
                <IconClose className={css.iconClose} />
              ) : (
                <IconSearch className={css.icon} />
              )}
            </button>
          )}
          <NamedLink
            className={css.iconButton}
            name="FavoriteListingsPage"
            ariaLabel={intl.formatMessage({ id: 'Topbar.actionFavorites' })}
          >
            <IconHeart className={css.icon} />
            {favoritesCount > 0 ? (
              <span className={css.favoritesBadge} aria-hidden="true">
                {favoritesCount > 99
                  ? intl.formatMessage({ id: 'FavoritesBadge.overflow' })
                  : intl.formatNumber(favoritesCount)}
              </span>
            ) : null}
          </NamedLink>
          <NamedLink
            className={classNames(css.iconButton, css.iconButtonMessages)}
            name="InboxPage"
            params={{ tab: 'sales' }}
            ariaLabel={intl.formatMessage({ id: 'Topbar.actionMessages' })}
          >
            <IconMessages className={css.icon} />
            {messagesNotificationCount > 0 ? (
              <span className={css.messagesBadge} aria-hidden="true">
                {messagesNotificationCount > 99
                  ? intl.formatMessage({ id: 'FavoritesBadge.overflow' })
                  : intl.formatNumber(messagesNotificationCount)}
              </span>
            ) : null}
          </NamedLink>

          {isAuthenticated ? (
            <UserMenu currentUser={currentUser} onLogout={onLogout} />
          ) : (
            <NamedLink
              className={css.iconButton}
              name="SignupPage"
              ariaLabel={intl.formatMessage({ id: 'Topbar.actionLogin' })}
            >
              <IconUser className={css.icon} />
            </NamedLink>
          )}
        </div>
      </div>
    </header>
  );
};

export default TopbarDesktop;
